import api, { unwrap } from './client';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type {
  SocialAccountRequest,
  SocialAccountResponse,
  SocialAnalyticsDashboard,
  SocialCalendarEntry,
  SocialCampaign,
  SocialCampaignRequest,
  SocialComment,
  SocialCompetitor,
  SocialCompetitorRequest,
  SocialConversationResponse,
  SocialDashboardSummary,
  SocialListeningKeyword,
  SocialListeningKeywordRequest,
  SocialListeningMatch,
  SocialMessageResponse,
  SocialMonitoringSummary,
  SocialPostRequest,
  SocialPostResponse,
  SocialReview,
  SocialBrand,
  SocialBrandRequest,
  SocialIntegrationConfig,
  SocialIntegrationConfigRequest,
  SocialManagedApp,
  SocialManagedAppRequest,
  SocialConnectAvailability,
  SocialConnectionFlow,
  SocialMetaAssetsResponse,
  SocialMetaBindAssetRequest,
  SocialConnectionOverview,
  SocialChannelHealth,
  SocialConnectionTestResult,
  SocialConnectionLog,
  SocialContentItem,
  SocialAutomationRule,
  SocialAutomationRuleRequest,
  SocialMediaFolder,
  SocialMediaAsset,
  SocialMediaAssetSearchParams,
  SocialMediaAssetUpdateRequest,
  SocialMediaStorageSummary,
  SocialMediaAccessResponse,
  SocialMediaUsageResponse,
  SocialReviewsSummary,
  SocialPlatformAnalytics,
  SocialContact,
  SocialContactSearchParams,
  SocialContactUpsertRequest,
  SocialContactTimelineEvent,
  SocialContactNamedResource,
  SocialContactNamedResourceRequest,
  SocialContactCustomFieldDefinition,
  SocialContactCustomFieldDefinitionRequest,
  ContactImportJob,
  ContactImportMappingRequest,
  ContactImportPreview,
  ContactImportRow,
  ContactImportRowStatus,
  ContactImportUploadResponse,
  WhatsAppTemplateDetail,
  WhatsAppTemplateSendCommand,
  WhatsAppTemplateSendJob,
  WhatsAppTemplateSummary,
  WhatsAppContactGroup,
  WhatsAppContactGroupCreate,
  WhatsAppContactGroupPatch,
  WhatsAppGroupMember,
  WhatsAppMembershipCommand,
  WhatsAppMembershipResult,
  WhatsAppGroupEvaluationRequest,
  WhatsAppGroupEvaluation,
  WhatsAppDraftGroupEvaluationRequest,
  WhatsAppDraftGroupEvaluation,
  WhatsAppAudienceSnapshotRequest,
  WhatsAppAudienceSnapshot,
  WhatsAppAudienceSnapshotMember,
  WhatsAppGroupAnalytics,
  WhatsAppGroupOverlap,
  SavedAudienceFilter,
  SavedAudienceFilterRequest,
  WhatsAppCampaign,
  WhatsAppCampaignAnalytics,
  WhatsAppCampaignComparison,
  WhatsAppCampaignDraft,
  WhatsAppCampaignHistoryEvent,
  WhatsAppCampaignRun,
  WhatsAppCampaignValidation,
  WhatsAppBulkQueue,
  WhatsAppBulkQueueItem,
  WhatsAppBulkFailedItem,
  WhatsAppSchedule,
  WhatsAppScheduleExecution,
  WhatsAppScheduleOccurrence,
  WhatsAppScheduleRequest,
  WhatsAppWebhookEventSummary,
  WhatsAppWebhookEventDetail,
  WhatsAppWebhookHealth,
  WhatsAppWebhookMetrics,
  WhatsAppSyncRunRequest,
  WhatsAppSyncRunSummary,
  WhatsAppSyncRunDetail,
  WhatsAppSyncPlanCommand,
  WhatsAppSyncPlan,
  WhatsAppSyncHealth,
  WhatsAppReportCatalogItem,
  WhatsAppReportJob,
  WhatsAppReportJobCreateRequest,
  WhatsAppReportSchedule,
  WhatsAppReportScheduleRequest,
  WhatsAppTenantSettings,
  WhatsAppTenantSettingsPatch,
  WhatsAppBusinessProfile,
  WhatsAppBusinessProfilePatch,
  WhatsAppWorkingHours,
  WhatsAppWorkingHoursUpsert,
  WhatsAppHoliday,
  WhatsAppWebhookSettings,
  WhatsAppApiToken,
  WhatsAppApiTokenCreateRequest,
  WhatsAppApiTokenCreateResponse,
  WhatsAppRolesLink,
  WhatsAppAuditLink,
} from '@/types/social';
import type {
  WhatsAppAiSentiment,
  WhatsAppAiSuggestion,
  WhatsAppAiSummary,
  WhatsAppAuditDetail,
  WhatsAppAuditExportCreateRequest,
  WhatsAppAuditExportJob,
  WhatsAppAuditExportsPage,
  WhatsAppAuditFilterParams,
  WhatsAppAuditIntegrityRun,
  WhatsAppAuditLogsPage,
  WhatsAppAuditSummary,
  WhatsAppIntegrationConnection,
  WhatsAppIntegrationConnectionCreate,
  WhatsAppIntegrationConnectionsPage,
  WhatsAppIntegrationFieldMapping,
  WhatsAppIntegrationHealthCheck,
  WhatsAppKbDocument,
  WhatsAppKbSearchResponse,
  WhatsAppKbSource,
  WhatsAppSurvey,
  WhatsAppSurveyAnalytics,
  WhatsAppSurveyCreate,
  WhatsAppSurveyDispatchRequest,
  WhatsAppSurveyDispatchResponse,
} from '@/types/whatsappAdvanced';
import type {
  WhatsAppAnalyticsFilter,
  WhatsAppAnalyticsResponse,
  WhatsAppAnalyticsSection,
} from '@/types/whatsappNotifications';

interface BackendWhatsAppSchedule {
  id: number;
  name: string;
  scheduleType: string;
  scheduleState: WhatsAppSchedule['state'];
  socialAccountId: number;
  templateVersionId: number;
  audienceType: string;
  contactIds?: number[];
  groupId?: number | null;
  variables?: Record<string, string>;
  timezone: string;
  localTime: string;
  startDate: string;
  endDate?: string | null;
  rrule?: string | null;
  triggerField?: string | null;
  dstPolicy: WhatsAppSchedule['dstPolicy'];
  misfirePolicy: WhatsAppSchedule['misfirePolicy'];
  maxOccurrences?: number;
  occurrenceCount?: number;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BackendScheduleOccurrence {
  localDate: string;
  localTime: string;
  utc: string;
  offset?: string;
  dstAdjusted?: boolean;
  resolution?: string | null;
}

interface BackendScheduleHistory {
  id: number;
  plannedAt: string;
  actualAt?: string | null;
  state: string;
  sendJobId?: number | null;
  recipientCount?: number;
  errorCode?: string | null;
  errorMessage?: string | null;
}

interface SpringPage<T> {
  content: T[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first?: boolean;
  last?: boolean;
}

export const socialApi = {
  dashboardSummary: () =>
    unwrap(api.get<ApiResponse<SocialDashboardSummary>>('/v1/social/dashboard/summary')),

  dashboardFull: (period?: string) =>
    unwrap(
      api.get<
        ApiResponse<{
          summary: SocialDashboardSummary;
          monitoring: SocialMonitoringSummary;
          analytics: SocialAnalyticsDashboard;
        }>
      >('/v1/social/dashboard/full', { params: period ? { period } : undefined }),
    ),

  monitoringSummary: () =>
    unwrap(api.get<ApiResponse<SocialMonitoringSummary>>('/v1/social/analytics/monitoring')),

  analyticsDashboard: () =>
    unwrap(api.get<ApiResponse<SocialAnalyticsDashboard>>('/v1/social/analytics/dashboard')),

  whatsappAnalytics: (section: WhatsAppAnalyticsSection, params?: WhatsAppAnalyticsFilter) =>
    unwrap(
      api.get<ApiResponse<WhatsAppAnalyticsResponse>>(`/v1/social/whatsapp/analytics/${section}`, {
        params,
      }),
    ),

  exportWhatsAppAnalytics: (body: WhatsAppAnalyticsFilter & { section?: WhatsAppAnalyticsSection }) =>
    api.post('/v1/social/whatsapp/analytics/exports', body, { responseType: 'blob' }),

  platformAnalytics: (platform: string) =>
    unwrap(api.get<ApiResponse<SocialPlatformAnalytics>>(`/v1/social/analytics/platform/${platform}`)),

  syncPlatformAnalytics: (platform: string) =>
    unwrap(api.post<ApiResponse<null>>(`/v1/social/analytics/platform/${platform}/sync`)),

  listAccounts: () =>
    unwrap(api.get<ApiResponse<SocialAccountResponse[]>>('/v1/social/accounts')),

  disconnectAccount: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/accounts/${id}`)),

  deleteAccountPermanently: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/accounts/${id}/permanent`)),

  getOAuthUrl: (accountId: number) =>
    unwrap(api.get<ApiResponse<{ url: string }>>(`/v1/social/accounts/${accountId}/oauth/url`)),

  demoConnect: (accountId: number) =>
    unwrap(api.post<ApiResponse<SocialAccountResponse>>(`/v1/social/accounts/${accountId}/oauth/demo-connect`)),

  connectAccount: (body: SocialAccountRequest) =>
    unwrap(api.post<ApiResponse<SocialAccountResponse>>('/v1/social/accounts', body)),

  reconnectAccount: (id: number) =>
    unwrap(api.post<ApiResponse<SocialAccountResponse>>(`/v1/social/accounts/${id}/reconnect`)),

  markConversationRead: (conversationId: number) =>
    unwrap(api.post<ApiResponse<null>>(`/v1/social/inbox/conversations/${conversationId}/read`)),

  simulateInbound: (socialAccountId: number, message: string, conversationId?: string) =>
    unwrap(
      api.post<ApiResponse<SocialConversationResponse>>('/v1/social/inbox/simulate-inbound', {
        socialAccountId,
        message,
        conversationId: conversationId?.trim() || `demo_${Date.now()}`,
      }),
    ),

  listConversations: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialConversationResponse>>>('/v1/social/inbox/conversations', { params })),

  listConversationsFiltered: (params?: import('@/types/social').SocialInboxFilterParams) =>
    unwrap(
      api.get<ApiResponse<PageResponse<SocialConversationResponse>>>(
        '/v1/social/inbox/conversations/filtered',
        { params },
      ),
    ),

  listConversationTimeline: (conversationId: number, params?: SearchParams) =>
    unwrap(
      api.get<ApiResponse<import('@/types/social').SocialTimelineItemResponse[]>>(
        `/v1/social/inbox/conversations/${conversationId}/timeline`,
        { params },
      ),
    ),

  archiveConversation: (conversationId: number, archived = true) =>
    unwrap(
      api.post<ApiResponse<SocialConversationResponse>>(
        `/v1/social/inbox/conversations/${conversationId}/archive`,
        null,
        { params: { archived } },
      ),
    ),

  favoriteConversation: (conversationId: number, favorite = true) =>
    unwrap(
      api.post<ApiResponse<SocialConversationResponse>>(
        `/v1/social/inbox/conversations/${conversationId}/favorite`,
        null,
        { params: { favorite } },
      ),
    ),

  listSavedReplies: () =>
    unwrap(api.get<ApiResponse<import('@/types/social').SocialSavedReply[]>>('/v1/social/inbox/saved-replies')),

  createSavedReply: (body: { title?: string; bodyText: string }) =>
    unwrap(api.post<ApiResponse<import('@/types/social').SocialSavedReply>>('/v1/social/inbox/saved-replies', body)),

  deleteSavedReply: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/inbox/saved-replies/${id}`)),

  searchInbox: (q: string, platform?: string) =>
    unwrap(
      api.get<ApiResponse<import('@/types/social').SocialSearchResult[]>>('/v1/social/inbox/search', {
        params: { q, platform },
      }),
    ),

  getInboxFeatures: () =>
    unwrap(api.get<ApiResponse<Record<string, boolean>>>('/v1/social/inbox/features')),

  uploadInboxMedia: (conversationId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(
      api.post<ApiResponse<SocialMediaAsset>>(
        `/v1/social/inbox/conversations/${conversationId}/upload-media`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300_000 },
      ),
    );
  },

  updateConversationContact: (
    conversationId: number,
    body: { participantName?: string; participantUsername?: string },
  ) =>
    unwrap(
      api.patch<ApiResponse<SocialConversationResponse>>(
        `/v1/social/inbox/conversations/${conversationId}/contact`,
        body,
      ),
    ),

  deleteInboxMessage: (messageId: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/inbox/messages/${messageId}`)),

  getAllPlatformCapabilities: () =>
    unwrap(api.get<ApiResponse<Record<string, Record<string, unknown>>>>('/v1/social/platforms/capabilities')),

  getPlatformCapabilities: (platform: string) =>
    unwrap(api.get<ApiResponse<Record<string, unknown>>>(`/v1/social/platforms/${platform}/capabilities`)),

  listMessages: (conversationId: number, params?: SearchParams) =>
    unwrap(
      api.get<ApiResponse<PageResponse<SocialMessageResponse>>>(
        `/v1/social/inbox/conversations/${conversationId}/messages`,
        { params },
      ),
    ),

  sendMessage: (
    conversationId: number,
    body: {
      messageContent?: string;
      messageType?: string;
      mediaUrl?: string;
      mimeType?: string;
      mediaFileName?: string;
      templateName?: string;
      templateLanguage?: string;
      replyToMessageId?: number;
    },
  ) =>
    unwrap(
      api.post<ApiResponse<SocialMessageResponse>>(
        `/v1/social/inbox/conversations/${conversationId}/messages`,
        body,
      ),
    ),

  listPosts: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialPostResponse>>>('/v1/social/posts', { params })),

  listContent: (params: { platform: string; type?: string }) =>
    unwrap(api.get<ApiResponse<SocialContentItem[]>>('/v1/social/content', { params })),

  syncContent: (platform: string) =>
    unwrap(api.post<ApiResponse<{ synced: number }>>('/v1/social/content/sync', null, { params: { platform } })),

  syncInbox: (platform: string) =>
    unwrap(api.post<ApiResponse<{ synced: number }>>('/v1/social/inbox/sync', null, { params: { platform } })),

  createPost: (body: SocialPostRequest) =>
    unwrap(api.post<ApiResponse<SocialPostResponse>>('/v1/social/posts', body)),

  publishPost: (id: number) =>
    unwrap(api.post<ApiResponse<SocialPostResponse>>(`/v1/social/posts/${id}/publish`)),

  approvePost: (id: number) =>
    unwrap(api.post<ApiResponse<SocialPostResponse>>(`/v1/social/posts/${id}/approve`)),

  uploadPostMedia: (postId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(api.post<ApiResponse<{ fileUrl: string }>>(`/v1/social/posts/${postId}/media`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300_000,
    }));
  },

  syncAnalytics: () => unwrap(api.post<ApiResponse<null>>('/v1/social/analytics/sync')),

  exportPostsCsv: () =>
    api.get('/v1/social/reports/posts/export.csv', { responseType: 'blob' }),

  exportInboxCsv: () =>
    api.get('/v1/social/reports/inbox/export.csv', { responseType: 'blob' }),

  listCampaigns: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialCampaign>>>('/v1/social/campaigns', { params })),

  createCampaign: (body: SocialCampaignRequest) =>
    unwrap(api.post<ApiResponse<SocialCampaign>>('/v1/social/campaigns', body)),

  submitCampaignApproval: (id: number) =>
    unwrap(api.post<ApiResponse<SocialCampaign>>(`/v1/social/campaigns/${id}/submit-approval`)),

  listWhatsAppCampaigns: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<WhatsAppCampaign>>>(
      '/v1/social/whatsapp/campaigns', { params },
    )),

  getWhatsAppCampaign: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppCampaign>>(`/v1/social/whatsapp/campaigns/${id}`)),

  createWhatsAppCampaign: (body: WhatsAppCampaignDraft) =>
    unwrap(api.post<ApiResponse<WhatsAppCampaign>>('/v1/social/whatsapp/campaigns', body)),

  updateWhatsAppCampaign: (id: number, body: WhatsAppCampaignDraft) =>
    unwrap(api.put<ApiResponse<WhatsAppCampaign>>(`/v1/social/whatsapp/campaigns/${id}`, body)),

  validateWhatsAppCampaign: (id: number) =>
    unwrap(api.post<ApiResponse<WhatsAppCampaignValidation>>(
      `/v1/social/whatsapp/campaigns/${id}/validate`,
    )),

  commandWhatsAppCampaign: (id: number, command: 'submit' | 'approve' | 'launch' | 'pause' | 'resume' | 'cancel' | 'clone') =>
    unwrap(api.post<ApiResponse<WhatsAppCampaign | WhatsAppCampaignRun>>(
      `/v1/social/whatsapp/campaigns/${id}/${command}`,
    )),

  scheduleWhatsAppCampaign: (id: number, scheduledAt: string, timezone: string) =>
    unwrap(api.post<ApiResponse<WhatsAppCampaign>>(
      `/v1/social/whatsapp/campaigns/${id}/schedule`, { scheduledAt, timezone },
    )),

  getWhatsAppCampaignHistory: (id: number, params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<WhatsAppCampaignHistoryEvent>>>(
      `/v1/social/whatsapp/campaigns/${id}/history`, { params },
    )),

  getWhatsAppCampaignRuns: (id: number, params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<WhatsAppCampaignRun>>>(
      `/v1/social/whatsapp/campaigns/${id}/runs`, { params },
    )),

  getWhatsAppCampaignAnalytics: (id: number, from?: string, to?: string) =>
    unwrap(api.get<ApiResponse<WhatsAppCampaignAnalytics>>(
      `/v1/social/whatsapp/campaigns/${id}/analytics`, { params: { from, to } },
    )),

  compareWhatsAppCampaigns: (body: {
    name?: string;
    campaignIds: number[];
    from?: string;
    to?: string;
    save?: boolean;
  }) =>
    unwrap(api.post<ApiResponse<WhatsAppCampaignComparison>>(
      '/v1/social/whatsapp/campaigns/compare', body,
    )),

  listWhatsAppBulkQueues: (params?: SearchParams & { state?: string }) =>
    unwrap(api.get<ApiResponse<PageResponse<WhatsAppBulkQueue>>>(
      '/v1/social/whatsapp/bulk-queues', { params },
    )),

  getWhatsAppBulkQueue: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppBulkQueue>>(`/v1/social/whatsapp/bulk-queues/${id}`)),

  listWhatsAppBulkQueueItems: (id: number, params?: SearchParams & { state?: string }) =>
    unwrap(api.get<ApiResponse<PageResponse<WhatsAppBulkQueueItem>>>(
      `/v1/social/whatsapp/bulk-queues/${id}/items`, { params },
    )),

  commandWhatsAppBulkQueue: (id: number, command: 'pause' | 'resume' | 'cancel') =>
    unwrap(api.post<ApiResponse<WhatsAppBulkQueue>>(
      `/v1/social/whatsapp/bulk-queues/${id}/${command}`,
    )),

  listWhatsAppBulkFailures: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<WhatsAppBulkFailedItem>>>(
      '/v1/social/whatsapp/bulk-queues/failed', { params },
    )),

  commandWhatsAppBulkItem: (id: number, command: 'retry' | 'discard') =>
    unwrap(api.post<ApiResponse<WhatsAppBulkQueueItem>>(
      `/v1/social/whatsapp/bulk-queues/items/${id}/${command}`,
    )),

  listWhatsAppSchedules: (params?: SearchParams & { state?: string; type?: string }) =>
    unwrap(api.get<ApiResponse<SpringPage<BackendWhatsAppSchedule>>>(
      '/v1/social/whatsapp/schedules', { params },
    )).then((page) => {
      const mapped = fromSpringPage(page, fromBackendSchedule);
      return params?.state
        ? { ...mapped, content: mapped.content.filter((item) => item.state === params.state) }
        : mapped;
    }),

  getWhatsAppSchedule: (id: number) =>
    unwrap(api.get<ApiResponse<BackendWhatsAppSchedule>>(
      `/v1/social/whatsapp/schedules/${id}`,
    )).then(fromBackendSchedule),

  createWhatsAppSchedule: (body: WhatsAppScheduleRequest) =>
    unwrap(api.post<ApiResponse<BackendWhatsAppSchedule>>(
      '/v1/social/whatsapp/schedules',
      toBackendScheduleRequest(body),
    )).then(fromBackendSchedule),

  updateWhatsAppSchedule: (id: number, body: WhatsAppScheduleRequest) =>
    unwrap(api.put<ApiResponse<BackendWhatsAppSchedule>>(
      `/v1/social/whatsapp/schedules/${id}`,
      toBackendScheduleRequest(body),
    )).then(fromBackendSchedule),

  deleteWhatsAppSchedule: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/whatsapp/schedules/${id}`)),

  commandWhatsAppSchedule: (id: number, command: 'pause' | 'resume' | 'cancel' | 'run-now') =>
    unwrap(api.post<ApiResponse<BackendWhatsAppSchedule | BackendScheduleHistory>>(
      `/v1/social/whatsapp/schedules/${id}/${command}`,
    )),

  previewWhatsAppScheduleOccurrences: (body: WhatsAppScheduleRequest, count = 5) =>
    unwrap(api.post<ApiResponse<BackendScheduleOccurrence[]>>(
      '/v1/social/whatsapp/schedules/preview-occurrences',
      {
        ...toBackendScheduleRequest(body),
        limit: count,
      },
    )).then((items) => items.map(fromBackendOccurrence)),

  getWhatsAppScheduleOccurrences: (id: number, count = 5) =>
    unwrap(api.get<ApiResponse<BackendScheduleOccurrence[]>>(
      `/v1/social/whatsapp/schedules/${id}/occurrences`, { params: { limit: count } },
    )).then((items) => items.map(fromBackendOccurrence)),

  getWhatsAppScheduleHistory: (id: number, params?: SearchParams) =>
    unwrap(api.get<ApiResponse<SpringPage<BackendScheduleHistory>>>(
      `/v1/social/whatsapp/schedules/${id}/history`, { params },
    )).then((page) => fromSpringPage(page, (item) => fromBackendHistory(item, id))),

  listReviews: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialReview>>>('/v1/social/reviews', { params })),

  listComments: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialComment>>>('/v1/social/reviews/comments', { params })),

  syncComments: (platform?: string) =>
    unwrap(
      api.post<ApiResponse<{ ingested: number }>>('/v1/social/reviews/comments/sync', null, {
        params: platform ? { platform } : undefined,
      }),
    ),

  replyComment: (id: number, replyText: string) =>
    unwrap(
      api.post<ApiResponse<SocialComment>>(`/v1/social/reviews/comments/${id}/reply`, { replyText }),
    ),

  replyReview: (id: number, replyText: string) =>
    unwrap(api.post<ApiResponse<SocialReview>>(`/v1/social/reviews/${id}/reply`, { replyText })),

  listListeningKeywords: () =>
    unwrap(api.get<ApiResponse<SocialListeningKeyword[]>>('/v1/social/listening/keywords')),

  addListeningKeyword: (body: SocialListeningKeywordRequest) =>
    unwrap(api.post<ApiResponse<SocialListeningKeyword>>('/v1/social/listening/keywords', body)),

  listListeningMatches: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialListeningMatch>>>('/v1/social/listening/matches', { params })),

  listCompetitors: () =>
    unwrap(api.get<ApiResponse<SocialCompetitor[]>>('/v1/social/listening/competitors')),

  addCompetitor: (body: SocialCompetitorRequest) =>
    unwrap(api.post<ApiResponse<SocialCompetitor>>('/v1/social/listening/competitors', body)),

  listBrands: () =>
    unwrap(api.get<ApiResponse<SocialBrand[]>>('/v1/social/setup/brands')),

  createBrand: (body: SocialBrandRequest) =>
    unwrap(api.post<ApiResponse<SocialBrand>>('/v1/social/setup/brands', body)),

  listCalendar: (from: string, to: string, page = 0, size = 100) =>
    unwrap(
      api.get<ApiResponse<PageResponse<SocialCalendarEntry>>>('/v1/social/setup/calendar', {
        params: { from, to, page, size },
      }),
    ),

  reschedulePost: (postId: number, scheduledAt: string) =>
    unwrap(
      api.post<ApiResponse<SocialCalendarEntry>>(`/v1/social/setup/calendar/${postId}/reschedule`, {
        scheduledAt,
      }),
    ),

  assignConversation: (id: number, employeeId: number, expectedVersion?: number) =>
    unwrap(
      api.post<ApiResponse<SocialConversationResponse>>(`/v1/social/conversations/${id}/assign`, {
        employeeId,
        expectedVersion,
      }),
    ),

  transferConversation: (
    id: number,
    body: { employeeId: number; reason?: string; version?: number },
  ) =>
    unwrap(
      api.post<ApiResponse<SocialConversationResponse>>(`/v1/social/conversations/${id}/transfer`, {
        employeeId: body.employeeId,
        reason: body.reason,
        expectedVersion: body.version,
      }),
    ),

  pinConversation: (id: number, pinned: boolean) =>
    unwrap(
      api.post<ApiResponse<{ pinned: boolean }>>(
        `/v1/social/conversations/${id}/pin`,
        null,
        { params: { pinned } },
      ),
    ),

  searchConversationMessages: (
    id: number,
    params: { q: string; page?: number; size?: number },
  ) =>
    unwrap(
      api.get<ApiResponse<PageResponse<SocialMessageResponse>>>(
        `/v1/social/conversations/${id}/messages/search`,
        { params },
      ),
    ).then((page) => ({
      ...page,
      content: page.content.map((message) => ({
        messageId: message.id,
        conversationId: message.conversationId,
        messageContent: message.messageContent,
        snippet: message.messageContent,
        senderType: message.senderType,
        sentAt: message.sentAt,
      })),
    })),

  listConversationLabels: (id: number) =>
    unwrap(
      api.get<ApiResponse<SocialContactNamedResource[]>>(
        `/v1/social/conversations/${id}/labels`,
      ),
    ),

  listLabels: () =>
    unwrap(api.get<ApiResponse<SocialContactNamedResource[]>>('/v1/social/contacts/labels')),

  updateConversationLabels: (id: number, labelIds: number[], expectedVersion?: number) =>
    unwrap(
      api.put<ApiResponse<SocialConversationResponse>>(`/v1/social/conversations/${id}/labels`, {
        labelIds,
        expectedVersion,
      }),
    ),

  setConversationTyping: (id: number, typing: boolean) =>
    unwrap(
      api.post<ApiResponse<null>>(`/v1/social/inbox/presence/conversations/${id}/typing`, {
        participantType: 'AGENT',
        participantId: 'current',
        typing,
      }),
    ),

  escalateConversation: (id: number) =>
    unwrap(api.post<ApiResponse<null>>(`/v1/social/setup/inbox/conversations/${id}/escalate`)),

  listIntegrationConfigs: () =>
    unwrap(api.get<ApiResponse<SocialIntegrationConfig[]>>('/v1/social/setup/integration-config')),

  saveIntegrationConfig: (body: SocialIntegrationConfigRequest) =>
    unwrap(api.post<ApiResponse<SocialIntegrationConfig>>('/v1/social/setup/integration-config', body)),

  listManagedApps: () =>
    unwrap(api.get<ApiResponse<SocialManagedApp[]>>('/v1/social/admin/managed-apps')),

  saveManagedApp: (platform: string, body: SocialManagedAppRequest) =>
    unwrap(api.put<ApiResponse<SocialManagedApp>>(`/v1/social/admin/managed-apps/${platform}`, body)),

  disableManagedApp: (platform: string) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/admin/managed-apps/${platform}`)),

  connectAvailability: () =>
    unwrap(api.get<ApiResponse<SocialConnectAvailability[]>>('/v1/social/connect/availability')),

  discoverMetaAssets: (accountId: number) =>
    unwrap(api.get<ApiResponse<SocialMetaAssetsResponse>>(`/v1/social/connect/accounts/${accountId}/assets`)),

  bindMetaAsset: (accountId: number, body: SocialMetaBindAssetRequest) =>
    unwrap(api.post<ApiResponse<SocialAccountResponse>>(`/v1/social/connect/accounts/${accountId}/bind`, body)),

  connectTelegramBot: (accountId: number) =>
    unwrap(api.post<ApiResponse<SocialAccountResponse>>(`/v1/social/connect/accounts/${accountId}/telegram`)),

  subscribeWebhooks: (accountId: number) =>
    unwrap(
      api.post<ApiResponse<SocialAccountResponse>>(`/v1/social/connect/accounts/${accountId}/subscribe-webhooks`),
    ),

  simulateReview: (socialAccountId: number, reviewText: string, rating = 4) =>
    unwrap(
      api.post<ApiResponse<SocialReview>>('/v1/social/reviews/simulate', {
        socialAccountId,
        reviewText,
        rating,
      }),
    ),

  listPendingApprovals: () =>
    unwrap(
      api.get<
        ApiResponse<
          { id: number; ownerModule: string; entityType: string; entityId: number; approvalStatus: string }[]
        >
      >('/v1/shared/approvals/pending', { params: { ownerModule: 'SOCIAL' } }),
    ),

  approveApprovalRequest: (id: number) =>
    unwrap(api.post<ApiResponse<{ status: string }>>(`/v1/shared/approvals/${id}/approve`)),

  rejectApprovalRequest: (id: number, notes?: string) =>
    unwrap(
      api.post<ApiResponse<{ status: string }>>(`/v1/shared/approvals/${id}/reject`, null, {
        params: notes ? { notes } : undefined,
      }),
    ),

  connectionOverview: () =>
    unwrap(api.get<ApiResponse<SocialConnectionOverview>>('/v1/social/connections/overview')),

  channelHealth: (accountId: number) =>
    unwrap(api.get<ApiResponse<SocialChannelHealth>>(`/v1/social/connections/health/${accountId}`)),

  refreshChannelToken: (accountId: number) =>
    unwrap(
      api.post<ApiResponse<SocialChannelHealth>>(`/v1/social/connections/accounts/${accountId}/refresh-token`),
    ),

  runConnectionTests: (accountId: number, type?: string) =>
    unwrap(
      api.post<ApiResponse<SocialConnectionTestResult[]>>(
        `/v1/social/connections/accounts/${accountId}/test`,
        null,
        { params: type ? { type } : undefined },
      ),
    ),

  connectionLogs: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialConnectionLog>>>('/v1/social/connections/logs', { params })),

  listAutomationRules: () =>
    unwrap(api.get<ApiResponse<SocialAutomationRule[]>>('/v1/social/automation/rules')),

  createAutomationRule: (body: SocialAutomationRuleRequest) =>
    unwrap(api.post<ApiResponse<SocialAutomationRule>>('/v1/social/automation/rules', body)),

  deleteAutomationRule: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/automation/rules/${id}`)),

  toggleAutomationRule: (id: number, active: boolean) =>
    unwrap(api.post<ApiResponse<SocialAutomationRule>>(`/v1/social/automation/rules/${id}/toggle`, { active })),

  listMediaFolders: () =>
    unwrap(api.get<ApiResponse<SocialMediaFolder[]>>('/v1/social/media/folders')),

  createMediaFolder: (name: string, parentFolderId?: number) =>
    unwrap(
      api.post<ApiResponse<SocialMediaFolder>>('/v1/social/media/folders', { name, parentFolderId }),
    ),

  updateMediaFolder: (id: number, body: { name: string; parentFolderId?: number | null }) =>
    unwrap(api.put<ApiResponse<SocialMediaFolder>>(`/v1/social/media/folders/${id}`, body)),

  deleteMediaFolder: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/media/folders/${id}`)),

  listMediaAssets: (params?: SearchParams & { folderId?: number }) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialMediaAsset>>>('/v1/social/media/assets', { params })),

  searchMediaAssets: (params?: SocialMediaAssetSearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialMediaAsset>>>('/v1/social/media/assets/search', { params })),

  uploadMediaAsset: (file: File, folderId?: number, tags?: string) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(
      api.post<ApiResponse<SocialMediaAsset>>('/v1/social/media/assets', form, {
        params: { folderId, tags },
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },

  updateMediaAsset: (id: number, body: SocialMediaAssetUpdateRequest) =>
    unwrap(api.patch<ApiResponse<SocialMediaAsset>>(`/v1/social/media/assets/${id}`, body)),

  deleteMediaAsset: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/media/assets/${id}`)),

  getMediaAssetUsage: (id: number) =>
    unwrap(api.get<ApiResponse<SocialMediaUsageResponse>>(`/v1/social/media/assets/${id}/usage`)),

  recordMediaAssetUsage: (
    id: number,
    body: { referenceType: string; referenceId: number; usageContext?: string },
  ) =>
    unwrap(api.post<ApiResponse<SocialMediaUsageResponse>>(`/v1/social/media/assets/${id}/usage`, body)),

  previewMediaAsset: (id: number) =>
    unwrap(api.get<ApiResponse<SocialMediaAccessResponse>>(`/v1/social/media/assets/${id}/preview`)),

  downloadMediaAsset: (id: number) =>
    unwrap(api.get<ApiResponse<SocialMediaAccessResponse>>(`/v1/social/media/assets/${id}/download`)),

  getMediaStorageSummary: () =>
    unwrap(api.get<ApiResponse<SocialMediaStorageSummary>>('/v1/social/media/storage')),

  reviewsSummary: () =>
    unwrap(api.get<ApiResponse<SocialReviewsSummary>>('/v1/social/reviews/summary')),

  assignComment: (id: number, employeeId: number) =>
    unwrap(api.post<ApiResponse<SocialComment>>(`/v1/social/reviews/comments/${id}/assign`, { employeeId })),

  resolveComment: (id: number) =>
    unwrap(api.post<ApiResponse<SocialComment>>(`/v1/social/reviews/comments/${id}/resolve`)),

  listInternalNotes: (entityType: string, entityId: number) =>
    unwrap(
      api.get<ApiResponse<{ id: number; entityType: string; entityId: number; noteText: string; createdAt?: string }[]>>(
        '/v1/social/setup/notes',
        { params: { entityType, entityId } },
      ),
    ),

  addInternalNote: (entityType: string, entityId: number, noteText: string) =>
    unwrap(
      api.post<ApiResponse<{ id: number; noteText: string; createdAt?: string }>>('/v1/social/setup/notes', {
        entityType,
        entityId,
        noteText,
      }),
    ),

  listTags: () =>
    unwrap(api.get<ApiResponse<{ id: number; name: string; color?: string }[]>>('/v1/social/setup/tags')),

  createTag: (name: string, color?: string) =>
    unwrap(api.post<ApiResponse<{ id: number; name: string }>>('/v1/social/setup/tags', { name, color })),

  listReactions: (entityType: string, entityId: number) =>
    unwrap(
      api.get<ApiResponse<Array<{ id: number; reactionType: string; platform: string; createdAt?: string }>>>(
        '/v1/social/engagement/reactions',
        { params: { entityType, entityId } },
      ),
    ),

  hideComment: (commentId: number) =>
    unwrap(api.post<ApiResponse<SocialComment>>(`/v1/social/reviews/comments/${commentId}/hide`)),

  listTemplates: (platform = 'WHATSAPP') =>
    unwrap(
      api.get<ApiResponse<Array<{ id: number; templateName: string; bodyText: string; approvalStatus: string }>>>(
        '/v1/social/templates',
        { params: { platform } },
      ),
    ),

  listWhatsAppTemplates: (params?: {
    accountId?: number;
    search?: string;
    category?: string;
    language?: string;
    status?: string;
    quality?: string;
  }) =>
    unwrap(
      api.get<ApiResponse<WhatsAppTemplateSummary[]>>('/v1/social/whatsapp/templates', { params }),
    ),

  getWhatsAppTemplate: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppTemplateDetail>>(`/v1/social/whatsapp/templates/${id}`)),

  syncWhatsAppTemplates: (socialAccountId: number, syncType = 'DELTA') =>
    unwrap(
      api.post<ApiResponse<{ id: number; state: string; templatesSeen: number }>>(
        '/v1/social/whatsapp/templates/sync',
        { socialAccountId, syncType },
      ),
    ),

  previewWhatsAppTemplate: (body: {
    templateId?: number;
    templateVersionId?: number;
    variables?: Record<string, string>;
    components?: Array<Record<string, unknown>>;
  }) =>
    unwrap(
      api.post<
        ApiResponse<{
          header: string;
          body: string;
          footer: string;
          buttons: Array<Record<string, unknown>>;
          missingVariables: string[];
          valid: boolean;
        }>
      >('/v1/social/whatsapp/templates/preview', body),
    ),

  getWhatsAppTemplateAnalytics: (id: number, from?: string, to?: string) =>
    unwrap(
      api.get<
        ApiResponse<{
          sent: number;
          delivered: number;
          read: number;
          failed: number;
          daily: Array<{ date: string; sent: number; delivered: number; read: number; failed: number }>;
        }>
      >(`/v1/social/whatsapp/templates/${id}/analytics`, { params: { from, to } }),
    ),

  validateWhatsAppTemplateSend: (body: WhatsAppTemplateSendCommand) =>
    unwrap(
      api.post<
        ApiResponse<{
          valid: boolean;
          total: number;
          eligible: number;
          ineligible: number;
          errors: Array<{ recipient: string; code: string; message: string; field?: string }>;
          estimatedCost: number;
        }>
      >('/v1/social/whatsapp/template-sends/validate', body),
    ),

  previewWhatsAppTemplateSend: (body: WhatsAppTemplateSendCommand) =>
    unwrap(api.post<ApiResponse<Record<string, unknown>>>('/v1/social/whatsapp/template-sends/preview', body)),

  createWhatsAppTemplateSend: (body: WhatsAppTemplateSendCommand) =>
    unwrap(
      api.post<ApiResponse<WhatsAppTemplateSendJob>>('/v1/social/whatsapp/template-sends/send', body),
    ),

  getWhatsAppTemplateSend: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppTemplateSendJob>>(`/v1/social/whatsapp/template-sends/${id}`)),

  listWhatsAppTemplateSendRecipients: (id: number, page = 0, size = 50) =>
    unwrap(
      api.get<ApiResponse<PageResponse<Record<string, unknown>>>>(
        `/v1/social/whatsapp/template-sends/${id}/recipients`,
        { params: { page, size } },
      ),
    ),

  controlWhatsAppTemplateSend: (
    id: number,
    action: 'pause' | 'resume' | 'cancel' | 'retry-failed',
  ) =>
    unwrap(
      api.post<ApiResponse<WhatsAppTemplateSendJob>>(
        `/v1/social/whatsapp/template-sends/${id}/${action}`,
      ),
    ),

  uploadWhatsAppTemplateAudience: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(
      api.post<
        ApiResponse<{
          headers: string[];
          recipients: Array<{ phone: string; variables: Record<string, string> }>;
          errors: Array<{ recipient: string; code: string; message: string; field?: string }>;
        }>
      >('/v1/social/whatsapp/template-sends/audience-upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300_000,
      }),
    );
  },

  listAuditLogs: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<{ id: number; action: string; detail?: string; createdAt?: string }>>>(
      '/v1/social/audit-logs',
      { params },
    )),

  draftPostWithAi: (prompt: string, tone?: string) =>
    unwrap(
      api.post<ApiResponse<{ id: number; suggestionText: string; sessionId: number }>>('/v1/social/ai/draft-post', {
        prompt,
        tone,
      }),
    ),

  draftReplyWithAi: (prompt: string, tone?: string) =>
    unwrap(
      api.post<ApiResponse<{ id: number; suggestionText: string; sessionId: number }>>('/v1/social/ai/draft-reply', {
        prompt,
        tone,
      }),
    ),

  executiveDashboard: () =>
    unwrap(
      api.get<
        ApiResponse<{
          reach: number;
          engagement: number;
          messages: number;
          conversations: number;
          publishedPosts: number;
          avgResponseMinutes: number | null;
          conversionRate: number;
        }>
      >('/v1/social/analytics/executive'),
    ),

  getConnectionFlow: (platform: string, accountId?: number | null) =>
    unwrap(
      api.get<ApiResponse<SocialConnectionFlow>>(`/v1/social/connect/flow/${platform}`, {
        params: accountId ? { accountId } : undefined,
      }),
    ),

  completeConnectionStep: (platform: string, stepId: string, accountId: number) =>
    unwrap(
      api.post<ApiResponse<null>>(`/v1/social/connect/flow/${platform}/steps/${stepId}`, null, {
        params: { accountId },
      }),
    ),

  resetConnectionFlow: (platform: string, accountId?: number | null) =>
    unwrap(
      api.post<ApiResponse<null>>(`/v1/social/connect/flow/${platform}/reset`, null, {
        params: accountId ? { accountId } : undefined,
      }),
    ),

  getConnectionNotificationPrefs: () =>
    unwrap(
      api.get<
        ApiResponse<{
          emailOnTokenExpiry?: boolean;
          emailOnWebhookFailure?: boolean;
          emailOnDisconnect?: boolean;
          inAppAlerts?: boolean;
        }>
      >('/v1/social/connect/notification-prefs'),
    ),

  saveConnectionNotificationPrefs: (body: {
    emailOnTokenExpiry?: boolean;
    emailOnWebhookFailure?: boolean;
    emailOnDisconnect?: boolean;
    inAppAlerts?: boolean;
  }) =>
    unwrap(api.put<ApiResponse<typeof body>>('/v1/social/connect/notification-prefs', body)),

  listRoutingRules: () =>
    unwrap(
      api.get<
        ApiResponse<
          Array<{
            id: number;
            name: string;
            departmentCode: string;
            priority: number;
            conditionJson?: string;
            active?: boolean;
          }>
        >
      >('/v1/social/routing-rules'),
    ),

  createRoutingRule: (body: {
    name: string;
    departmentCode: string;
    priority?: number;
    conditionJson?: string;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<{ id: number }>>('/v1/social/routing-rules', body)),

  listWorkflows: () =>
    unwrap(
      api.get<
        ApiResponse<Array<{ id: number; name: string; workflowJson: string; active?: boolean }>>
      >('/v1/social/workflows'),
    ),

  createWorkflow: (body: { name: string; workflowJson: string; active?: boolean }) =>
    unwrap(api.post<ApiResponse<{ id: number; name: string }>>('/v1/social/workflows', body)),

  updateWorkflow: (id: number, body: { name?: string; workflowJson?: string; active?: boolean }) =>
    unwrap(api.patch<ApiResponse<{ id: number }>>(`/v1/social/workflows/${id}`, body)),

  deleteWorkflow: (id: number) => unwrap(api.delete<ApiResponse<null>>(`/v1/social/workflows/${id}`)),

  listOutboundWebhooks: () =>
    unwrap(
      api.get<
        ApiResponse<Array<{ id: number; name: string; targetUrl: string; eventTypes: string; active?: boolean }>>
      >('/v1/social/outbound-webhooks'),
    ),

  createOutboundWebhook: (body: { name: string; targetUrl: string; eventTypes: string; active?: boolean }) =>
    unwrap(api.post<ApiResponse<{ id: number }>>('/v1/social/outbound-webhooks', body)),

  listContacts: (params?: SocialContactSearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialContact>>>('/v1/social/contacts', { params })),

  getContact: (id: number) =>
    unwrap(api.get<ApiResponse<SocialContact>>(`/v1/social/contacts/${id}`)),

  createContact: (body: SocialContactUpsertRequest) =>
    unwrap(api.post<ApiResponse<SocialContact>>('/v1/social/contacts', body)),

  updateContact: (id: number, body: SocialContactUpsertRequest) =>
    unwrap(api.put<ApiResponse<SocialContact>>(`/v1/social/contacts/${id}`, body)),

  deleteContact: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/contacts/${id}`)),

  findContactDuplicates: (phone: string, defaultRegion?: string) =>
    unwrap(api.get<ApiResponse<SocialContact[]>>('/v1/social/contacts/duplicates', {
      params: { phone, defaultRegion },
    })),

  mergeContacts: (sourceContactId: number, targetContactId: number) =>
    unwrap(api.post<ApiResponse<SocialContact>>(`/v1/social/contacts/${sourceContactId}/merge`, {
      targetContactId,
    })),

  contactTimeline: (id: number, page = 0, size = 20) =>
    unwrap(api.get<ApiResponse<PageResponse<SocialContactTimelineEvent>>>(
      `/v1/social/contacts/${id}/timeline`,
      { params: { page, size } },
    )),

  updateContactRelationships: (id: number, tagIds: number[], labelIds: number[]) =>
    unwrap(api.put<ApiResponse<SocialContact>>(`/v1/social/contacts/${id}/relationships`, {
      tagIds,
      labelIds,
    })),

  setContactFavorite: (id: number, value: boolean) =>
    unwrap(api.put<ApiResponse<SocialContact>>(`/v1/social/contacts/${id}/favorite`, { value })),

  setContactBlocked: (id: number, value: boolean) =>
    unwrap(api.put<ApiResponse<SocialContact>>(`/v1/social/contacts/${id}/blocked`, { value })),

  assignContact: (id: number, employeeId: number | null) =>
    unwrap(api.put<ApiResponse<SocialContact>>(`/v1/social/contacts/${id}/assignment`, { employeeId })),

  exportContactsCsv: (params?: SocialContactSearchParams) =>
    api.get<Blob>('/v1/social/contacts/export.csv', { params, responseType: 'blob' }),

  listContactTags: () =>
    unwrap(api.get<ApiResponse<SocialContactNamedResource[]>>('/v1/social/contacts/tags')),

  createContactTag: (body: SocialContactNamedResourceRequest) =>
    unwrap(api.post<ApiResponse<SocialContactNamedResource>>('/v1/social/contacts/tags', body)),

  listContactLabels: () =>
    unwrap(api.get<ApiResponse<SocialContactNamedResource[]>>('/v1/social/contacts/labels')),

  createContactLabel: (body: SocialContactNamedResourceRequest) =>
    unwrap(api.post<ApiResponse<SocialContactNamedResource>>('/v1/social/contacts/labels', body)),

  listContactCustomFieldDefinitions: () =>
    unwrap(api.get<ApiResponse<SocialContactCustomFieldDefinition[]>>(
      '/v1/social/contacts/custom-field-definitions',
    )),

  createContactCustomFieldDefinition: (body: SocialContactCustomFieldDefinitionRequest) =>
    unwrap(api.post<ApiResponse<SocialContactCustomFieldDefinition>>(
      '/v1/social/contacts/custom-field-definitions',
      body,
    )),

  uploadContactImport: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(api.post<ApiResponse<ContactImportUploadResponse>>(
      '/v1/social/whatsapp/contact-imports/upload',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300_000 },
    ));
  },

  updateContactImportMapping: (id: number, body: ContactImportMappingRequest) =>
    unwrap(api.put<ApiResponse<ContactImportJob>>(
      `/v1/social/whatsapp/contact-imports/${id}/mapping`,
      body,
    )),

  previewContactImport: (id: number) =>
    unwrap(api.post<ApiResponse<ContactImportPreview>>(
      `/v1/social/whatsapp/contact-imports/${id}/preview`,
    )),

  startContactImport: (id: number) =>
    unwrap(api.post<ApiResponse<ContactImportJob>>(
      `/v1/social/whatsapp/contact-imports/${id}/start`,
    )),

  cancelContactImport: (id: number) =>
    unwrap(api.post<ApiResponse<ContactImportJob>>(
      `/v1/social/whatsapp/contact-imports/${id}/cancel`,
    )),

  getContactImport: (id: number) =>
    unwrap(api.get<ApiResponse<ContactImportJob>>(
      `/v1/social/whatsapp/contact-imports/${id}`,
    )),

  listContactImportHistory: (page = 0, size = 20) =>
    unwrap(api.get<ApiResponse<PageResponse<ContactImportJob>>>(
      '/v1/social/whatsapp/contact-imports/history',
      { params: { page, size } },
    )),

  listContactImportRows: (
    id: number,
    params?: { status?: ContactImportRowStatus; page?: number; size?: number },
  ) =>
    unwrap(api.get<ApiResponse<PageResponse<ContactImportRow>>>(
      `/v1/social/whatsapp/contact-imports/${id}/rows`,
      { params },
    )),

  exportFailedContactImportRows: (id: number) =>
    api.post<Blob>(
      `/v1/social/whatsapp/contact-imports/${id}/failed-export`,
      undefined,
      { responseType: 'blob' },
    ),

  listWhatsAppGroups: (page = 0, size = 50) =>
    unwrap(api.get<ApiResponse<PageResponse<WhatsAppContactGroup>>>(
      '/v1/social/whatsapp/groups',
      { params: { page, size } },
    )),

  getWhatsAppGroup: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppContactGroup>>(`/v1/social/whatsapp/groups/${id}`)),

  createWhatsAppGroup: (body: WhatsAppContactGroupCreate) =>
    unwrap(api.post<ApiResponse<WhatsAppContactGroup>>('/v1/social/whatsapp/groups', body)),

  updateWhatsAppGroup: (id: number, body: WhatsAppContactGroupPatch) =>
    unwrap(api.patch<ApiResponse<WhatsAppContactGroup>>(`/v1/social/whatsapp/groups/${id}`, body)),

  deleteWhatsAppGroup: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/whatsapp/groups/${id}`)),

  listWhatsAppGroupMembers: (id: number, page = 0, size = 50) =>
    unwrap(api.get<ApiResponse<PageResponse<WhatsAppGroupMember>>>(
      `/v1/social/whatsapp/groups/${id}/members`,
      { params: { page, size } },
    )),

  updateWhatsAppGroupMembers: (id: number, body: WhatsAppMembershipCommand) =>
    unwrap(api.put<ApiResponse<WhatsAppMembershipResult>>(
      `/v1/social/whatsapp/groups/${id}/members`,
      body,
    )),

  removeWhatsAppGroupMembers: (id: number, contactIds: number[]) =>
    unwrap(api.delete<ApiResponse<WhatsAppMembershipResult>>(
      `/v1/social/whatsapp/groups/${id}/members`,
      { data: { operation: 'REMOVE', contactIds } satisfies WhatsAppMembershipCommand },
    )),

  evaluateWhatsAppGroup: (id: number, body?: WhatsAppGroupEvaluationRequest) =>
    unwrap(api.post<ApiResponse<WhatsAppGroupEvaluation>>(
      `/v1/social/whatsapp/groups/${id}/evaluate`,
      body,
    )),

  evaluateDraftWhatsAppGroup: (body: WhatsAppDraftGroupEvaluationRequest) =>
    unwrap(api.post<ApiResponse<WhatsAppDraftGroupEvaluation>>(
      '/v1/social/whatsapp/groups/evaluate',
      body,
    )),

  exportWhatsAppGroupMembers: (id: number) =>
    api.get<Blob>(`/v1/social/whatsapp/groups/${id}/members/export.csv`, {
      responseType: 'blob',
    }),

  createWhatsAppGroupSnapshot: (id: number, body?: WhatsAppAudienceSnapshotRequest) =>
    unwrap(api.post<ApiResponse<WhatsAppAudienceSnapshot>>(
      `/v1/social/whatsapp/groups/${id}/snapshot`,
      body,
    )),

  getWhatsAppGroupSnapshot: (groupId: number, snapshotId: number) =>
    unwrap(api.get<ApiResponse<WhatsAppAudienceSnapshot>>(
      `/v1/social/whatsapp/groups/${groupId}/snapshots/${snapshotId}`,
    )),

  listWhatsAppGroupSnapshotMembers: (
    groupId: number,
    snapshotId: number,
    page = 0,
    size = 50,
    includePhone = false,
  ) =>
    unwrap(api.get<ApiResponse<PageResponse<WhatsAppAudienceSnapshotMember>>>(
      `/v1/social/whatsapp/groups/${groupId}/snapshots/${snapshotId}/members`,
      { params: { page, size, includePhone } },
    )),

  getWhatsAppGroupAnalytics: (id: number, from?: string, to?: string) =>
    unwrap(api.get<ApiResponse<WhatsAppGroupAnalytics>>(
      `/v1/social/whatsapp/groups/${id}/analytics`,
      { params: { from, to } },
    )),

  getWhatsAppGroupOverlap: (id: number, otherId: number) =>
    unwrap(api.get<ApiResponse<WhatsAppGroupOverlap>>(
      `/v1/social/whatsapp/groups/${id}/overlap/${otherId}`,
    )),

  listSavedAudienceFilters: () =>
    unwrap(api.get<ApiResponse<SavedAudienceFilter[]>>('/v1/social/whatsapp/saved-filters')),

  getSavedAudienceFilter: (id: number) =>
    unwrap(api.get<ApiResponse<SavedAudienceFilter>>(`/v1/social/whatsapp/saved-filters/${id}`)),

  createSavedAudienceFilter: (body: SavedAudienceFilterRequest) =>
    unwrap(api.post<ApiResponse<SavedAudienceFilter>>('/v1/social/whatsapp/saved-filters', body)),

  updateSavedAudienceFilter: (id: number, body: SavedAudienceFilterRequest) =>
    unwrap(api.put<ApiResponse<SavedAudienceFilter>>(`/v1/social/whatsapp/saved-filters/${id}`, body)),

  deleteSavedAudienceFilter: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/whatsapp/saved-filters/${id}`)),

  listWhatsAppWebhookEvents: (params?: {
    state?: string;
    accountId?: number;
    eventType?: string;
    page?: number;
    size?: number;
  }) =>
    unwrap(api.get<ApiResponse<SpringPage<WhatsAppWebhookEventSummary>>>(
      '/v1/social/whatsapp/webhook-events',
      { params },
    )).then((page) => fromSpringPage(page, (item) => item)),

  getWhatsAppWebhookEvent: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppWebhookEventDetail>>(
      `/v1/social/whatsapp/webhook-events/${id}`,
    )),

  replayWhatsAppWebhookEvent: (id: number) =>
    unwrap(api.post<ApiResponse<null>>(`/v1/social/whatsapp/webhook-events/${id}/replay`)),

  getWhatsAppWebhookHealth: () =>
    unwrap(api.get<ApiResponse<WhatsAppWebhookHealth>>('/v1/social/whatsapp/webhook-events/health')),

  getWhatsAppWebhookMetrics: () =>
    unwrap(api.get<ApiResponse<WhatsAppWebhookMetrics>>('/v1/social/whatsapp/webhook-events/metrics')),

  createWhatsAppSyncRun: (body: WhatsAppSyncRunRequest) =>
    unwrap(api.post<ApiResponse<WhatsAppSyncRunSummary>>('/v1/social/whatsapp/sync-runs', body)),

  listWhatsAppSyncRuns: (params?: { page?: number; size?: number }) =>
    unwrap(api.get<ApiResponse<SpringPage<WhatsAppSyncRunSummary>>>(
      '/v1/social/whatsapp/sync-runs',
      { params },
    )).then((page) => fromSpringPage(page, (item) => item)),

  getWhatsAppSyncRun: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppSyncRunDetail>>(`/v1/social/whatsapp/sync-runs/${id}`)),

  cancelWhatsAppSyncRun: (id: number) =>
    unwrap(api.post<ApiResponse<WhatsAppSyncRunSummary>>(`/v1/social/whatsapp/sync-runs/${id}/cancel`)),

  retryWhatsAppSyncErrors: (id: number) =>
    unwrap(api.post<ApiResponse<WhatsAppSyncRunSummary>>(
      `/v1/social/whatsapp/sync-runs/${id}/retry-errors`,
    )),

  listWhatsAppSyncPlans: () =>
    unwrap(api.get<ApiResponse<WhatsAppSyncPlan[]>>('/v1/social/whatsapp/sync-plans')),

  getWhatsAppSyncPlan: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppSyncPlan>>(`/v1/social/whatsapp/sync-plans/${id}`)),

  createWhatsAppSyncPlan: (body: WhatsAppSyncPlanCommand) =>
    unwrap(api.post<ApiResponse<WhatsAppSyncPlan>>('/v1/social/whatsapp/sync-plans', body)),

  updateWhatsAppSyncPlan: (id: number, body: WhatsAppSyncPlanCommand) =>
    unwrap(api.put<ApiResponse<WhatsAppSyncPlan>>(`/v1/social/whatsapp/sync-plans/${id}`, body)),

  deleteWhatsAppSyncPlan: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/whatsapp/sync-plans/${id}`)),

  getWhatsAppSyncHealth: (socialAccountId: number) =>
    unwrap(api.get<ApiResponse<WhatsAppSyncHealth>>('/v1/social/whatsapp/sync-health', {
      params: { socialAccountId },
    })),

  getWhatsAppSettings: () =>
    unwrap(api.get<ApiResponse<WhatsAppTenantSettings>>('/v1/social/whatsapp/settings')),

  patchWhatsAppSettings: (body: WhatsAppTenantSettingsPatch) =>
    unwrap(api.patch<ApiResponse<WhatsAppTenantSettings>>('/v1/social/whatsapp/settings', body)),

  getWhatsAppBusinessProfile: (accountId: number) =>
    unwrap(api.get<ApiResponse<WhatsAppBusinessProfile>>(
      `/v1/social/whatsapp/settings/accounts/${accountId}/business-profile`,
    )),

  patchWhatsAppBusinessProfile: (accountId: number, body: WhatsAppBusinessProfilePatch) =>
    unwrap(api.patch<ApiResponse<WhatsAppBusinessProfile>>(
      `/v1/social/whatsapp/settings/accounts/${accountId}/business-profile`,
      body,
    )),

  getWhatsAppWorkingHours: (accountId: number) =>
    unwrap(api.get<ApiResponse<WhatsAppWorkingHours>>(
      `/v1/social/whatsapp/settings/accounts/${accountId}/working-hours`,
    )),

  putWhatsAppWorkingHours: (accountId: number, body: WhatsAppWorkingHoursUpsert) =>
    unwrap(api.put<ApiResponse<WhatsAppWorkingHours>>(
      `/v1/social/whatsapp/settings/accounts/${accountId}/working-hours`,
      body,
    )),

  listWhatsAppHolidays: (accountId: number) =>
    unwrap(api.get<ApiResponse<WhatsAppHoliday[]>>(
      `/v1/social/whatsapp/settings/accounts/${accountId}/working-hours/holidays`,
    )),

  createWhatsAppHoliday: (accountId: number, body: { holidayDate: string; name: string; recurringYearly?: boolean }) =>
    unwrap(api.post<ApiResponse<WhatsAppHoliday>>(
      `/v1/social/whatsapp/settings/accounts/${accountId}/working-hours/holidays`,
      body,
    )),

  deleteWhatsAppHoliday: (accountId: number, holidayId: number) =>
    unwrap(api.delete<ApiResponse<null>>(
      `/v1/social/whatsapp/settings/accounts/${accountId}/working-hours/holidays/${holidayId}`,
    )),

  getWhatsAppWebhookSettings: (accountId: number) =>
    unwrap(api.get<ApiResponse<WhatsAppWebhookSettings>>(
      `/v1/social/whatsapp/settings/accounts/${accountId}/webhook-settings`,
    )),

  listWhatsAppApiTokens: () =>
    unwrap(api.get<ApiResponse<WhatsAppApiToken[]>>('/v1/social/whatsapp/settings/tokens')),

  createWhatsAppApiToken: (body: WhatsAppApiTokenCreateRequest) =>
    unwrap(api.post<ApiResponse<WhatsAppApiTokenCreateResponse>>(
      '/v1/social/whatsapp/settings/tokens',
      body,
    )),

  rotateWhatsAppApiToken: (id: number) =>
    unwrap(api.post<ApiResponse<WhatsAppApiTokenCreateResponse>>(
      `/v1/social/whatsapp/settings/tokens/${id}/rotate`,
    )),

  revokeWhatsAppApiToken: (id: number) =>
    unwrap(api.post<ApiResponse<WhatsAppApiToken>>(
      `/v1/social/whatsapp/settings/tokens/${id}/revoke`,
    )),

  getWhatsAppRolesLink: () =>
    unwrap(api.get<ApiResponse<WhatsAppRolesLink>>('/v1/social/whatsapp/settings/roles-link')),

  getWhatsAppAuditLink: () =>
    unwrap(api.get<ApiResponse<WhatsAppAuditLink>>('/v1/social/whatsapp/settings/audit-link')),

  whatsappReportCatalog: () =>
    unwrap(api.get<ApiResponse<WhatsAppReportCatalogItem[]>>('/v1/social/whatsapp/reports/catalog')),

  createWhatsAppReportJob: (body: WhatsAppReportJobCreateRequest) =>
    unwrap(api.post<ApiResponse<WhatsAppReportJob>>('/v1/social/whatsapp/reports/jobs', body)),

  listWhatsAppReportJobs: (params?: { page?: number; size?: number }) =>
    unwrap(api.get<ApiResponse<PageResponse<WhatsAppReportJob>>>(
      '/v1/social/whatsapp/reports/jobs',
      { params },
    )),

  getWhatsAppReportJob: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppReportJob>>(`/v1/social/whatsapp/reports/jobs/${id}`)),

  cancelWhatsAppReportJob: (id: number) =>
    unwrap(api.post<ApiResponse<WhatsAppReportJob>>(`/v1/social/whatsapp/reports/jobs/${id}/cancel`)),

  retryWhatsAppReportJob: (id: number) =>
    unwrap(api.post<ApiResponse<WhatsAppReportJob>>(`/v1/social/whatsapp/reports/jobs/${id}/retry`)),

  downloadWhatsAppReportJob: (id: number) =>
    api.get(`/v1/social/whatsapp/reports/jobs/${id}/download`, { responseType: 'blob' })
      .then((response) => response.data as Blob),

  listWhatsAppReportSchedules: () =>
    unwrap(api.get<ApiResponse<WhatsAppReportSchedule[]>>('/v1/social/whatsapp/reports/schedules')),

  createWhatsAppReportSchedule: (body: WhatsAppReportScheduleRequest) =>
    unwrap(api.post<ApiResponse<WhatsAppReportSchedule>>('/v1/social/whatsapp/reports/schedules', body)),

  updateWhatsAppReportSchedule: (id: number, body: WhatsAppReportScheduleRequest) =>
    unwrap(api.patch<ApiResponse<WhatsAppReportSchedule>>(
      `/v1/social/whatsapp/reports/schedules/${id}`,
      body,
    )),

  deleteWhatsAppReportSchedule: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/whatsapp/reports/schedules/${id}`)),

  listWhatsAppAuditLogs: (params?: WhatsAppAuditFilterParams) =>
    unwrap(api.get<ApiResponse<WhatsAppAuditLogsPage>>('/v1/social/whatsapp/audit-logs', { params })),

  getWhatsAppAuditLog: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppAuditDetail>>(`/v1/social/whatsapp/audit-logs/${id}`)),

  listWhatsAppAuditByCorrelation: (correlationId: string) =>
    unwrap(
      api.get<ApiResponse<WhatsAppAuditSummary[]>>(
        `/v1/social/whatsapp/audit-logs/correlation/${encodeURIComponent(correlationId)}`,
      ),
    ),

  createWhatsAppAuditExport: (body: WhatsAppAuditExportCreateRequest) =>
    unwrap(
      api.post<ApiResponse<WhatsAppAuditExportJob>>('/v1/social/whatsapp/audit-logs/exports', body),
    ),

  listWhatsAppAuditExports: (params?: { page?: number; size?: number }) =>
    unwrap(
      api.get<ApiResponse<WhatsAppAuditExportsPage>>('/v1/social/whatsapp/audit-logs/exports', {
        params,
      }),
    ),

  downloadWhatsAppAuditExport: (id: number) =>
    api
      .get(`/v1/social/whatsapp/audit-logs/exports/${id}/download`, { responseType: 'blob' })
      .then((response) => response.data as Blob),

  verifyWhatsAppAuditIntegrity: () =>
    unwrap(
      api.post<ApiResponse<WhatsAppAuditIntegrityRun>>(
        '/v1/social/whatsapp/audit-logs/integrity/verify',
      ),
    ),

  listWhatsAppIntegrationConnections: (params?: { page?: number; size?: number }) =>
    unwrap(
      api.get<ApiResponse<WhatsAppIntegrationConnectionsPage>>(
        '/v1/social/whatsapp/integrations/connections',
        { params },
      ),
    ),

  createWhatsAppIntegrationConnection: (body: WhatsAppIntegrationConnectionCreate) =>
    unwrap(
      api.post<ApiResponse<WhatsAppIntegrationConnection>>(
        '/v1/social/whatsapp/integrations/connections',
        body,
      ),
    ),

  getWhatsAppIntegrationConnection: (id: number) =>
    unwrap(
      api.get<ApiResponse<WhatsAppIntegrationConnection>>(
        `/v1/social/whatsapp/integrations/connections/${id}`,
      ),
    ),

  deleteWhatsAppIntegrationConnection: (id: number) =>
    unwrap(
      api.delete<ApiResponse<null>>(`/v1/social/whatsapp/integrations/connections/${id}`),
    ),

  testWhatsAppIntegrationConnection: (id: number) =>
    unwrap(
      api.post<ApiResponse<WhatsAppIntegrationHealthCheck>>(
        `/v1/social/whatsapp/integrations/connections/${id}/test`,
      ),
    ),

  syncWhatsAppIntegrationConnection: (id: number) =>
    unwrap(
      api.post<ApiResponse<{ deliveryId: number; state: string; correlationId?: string }>>(
        `/v1/social/whatsapp/integrations/connections/${id}/sync`,
      ),
    ),

  listWhatsAppIntegrationMappings: (connectionId: number) =>
    unwrap(
      api.get<ApiResponse<WhatsAppIntegrationFieldMapping[]>>(
        `/v1/social/whatsapp/integrations/connections/${connectionId}/mappings`,
      ),
    ),

  saveWhatsAppIntegrationMappings: (
    connectionId: number,
    mappings: WhatsAppIntegrationFieldMapping[],
  ) =>
    unwrap(
      api.put<ApiResponse<WhatsAppIntegrationFieldMapping[]>>(
        `/v1/social/whatsapp/integrations/connections/${connectionId}/mappings`,
        mappings,
      ),
    ),

  listWhatsAppKbSources: () =>
    unwrap(
      api.get<ApiResponse<WhatsAppKbSource[]>>('/v1/social/whatsapp/knowledge-base/sources'),
    ),

  createWhatsAppKbSource: (body: { name: string; sourceType?: string; configJson?: Record<string, unknown> }) =>
    unwrap(
      api.post<ApiResponse<WhatsAppKbSource>>('/v1/social/whatsapp/knowledge-base/sources', body),
    ),

  deleteWhatsAppKbSource: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/whatsapp/knowledge-base/sources/${id}`)),

  listWhatsAppKbDocuments: (sourceId: number) =>
    unwrap(
      api.get<ApiResponse<WhatsAppKbDocument[]>>(
        `/v1/social/whatsapp/knowledge-base/sources/${sourceId}/documents`,
      ),
    ),

  createWhatsAppKbDocument: (
    sourceId: number,
    body: { title: string; contentText?: string },
  ) =>
    unwrap(
      api.post<ApiResponse<WhatsAppKbDocument>>(
        `/v1/social/whatsapp/knowledge-base/sources/${sourceId}/documents`,
        body,
      ),
    ),

  ingestWhatsAppKbDocument: (documentId: number) =>
    unwrap(
      api.post<ApiResponse<WhatsAppKbDocument>>(
        `/v1/social/whatsapp/knowledge-base/documents/${documentId}/ingest`,
      ),
    ),

  searchWhatsAppKb: (query: string, limit?: number) =>
    unwrap(
      api.get<ApiResponse<WhatsAppKbSearchResponse>>('/v1/social/whatsapp/knowledge-base/search', {
        params: { q: query, limit },
      }),
    ),

  listWhatsAppSurveys: () =>
    unwrap(api.get<ApiResponse<WhatsAppSurvey[]>>('/v1/social/whatsapp/surveys')),

  createWhatsAppSurvey: (body: WhatsAppSurveyCreate) =>
    unwrap(api.post<ApiResponse<WhatsAppSurvey>>('/v1/social/whatsapp/surveys', body)),

  deleteWhatsAppSurvey: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/social/whatsapp/surveys/${id}`)),

  dispatchWhatsAppSurvey: (id: number, body?: WhatsAppSurveyDispatchRequest) =>
    unwrap(
      api.post<ApiResponse<WhatsAppSurveyDispatchResponse>>(
        `/v1/social/whatsapp/surveys/${id}/dispatch`,
        body ?? {},
      ),
    ),

  getWhatsAppSurveyAnalytics: (id: number) =>
    unwrap(
      api.get<ApiResponse<WhatsAppSurveyAnalytics>>(`/v1/social/whatsapp/surveys/${id}/analytics`),
    ),

  createWhatsAppAiSuggestion: (conversationId: number, body?: { prompt?: string; context?: Record<string, unknown> }) =>
    unwrap(
      api.post<ApiResponse<WhatsAppAiSuggestion>>(
        `/v1/social/whatsapp/conversations/${conversationId}/ai/suggestions`,
        body ?? {},
      ),
    ),

  listWhatsAppAiSuggestions: (conversationId: number) =>
    unwrap(
      api.get<ApiResponse<WhatsAppAiSuggestion[]>>(
        `/v1/social/whatsapp/conversations/${conversationId}/ai/suggestions`,
      ),
    ),

  approveWhatsAppAiSuggestion: (conversationId: number, suggestionId: number) =>
    unwrap(
      api.post<ApiResponse<WhatsAppAiSuggestion>>(
        `/v1/social/whatsapp/conversations/${conversationId}/ai/suggestions/${suggestionId}/approve`,
      ),
    ),

  sendWhatsAppAiSuggestion: (conversationId: number, suggestionId: number) =>
    unwrap(
      api.post<ApiResponse<WhatsAppAiSuggestion>>(
        `/v1/social/whatsapp/conversations/${conversationId}/ai/suggestions/${suggestionId}/send`,
      ),
    ),

  summarizeWhatsAppConversation: (
    conversationId: number,
    body?: { scope?: string; maxMessages?: number },
  ) =>
    unwrap(
      api.post<ApiResponse<WhatsAppAiSummary>>(
        `/v1/social/whatsapp/conversations/${conversationId}/ai/summary`,
        body ?? { scope: 'FULL', maxMessages: 50 },
      ),
    ),

  getWhatsAppConversationSummary: (conversationId: number) =>
    unwrap(
      api.get<ApiResponse<WhatsAppAiSummary>>(
        `/v1/social/whatsapp/conversations/${conversationId}/ai/summary`,
      ),
    ),

  getWhatsAppConversationSentiment: (conversationId: number) =>
    unwrap(
      api.get<ApiResponse<WhatsAppAiSentiment>>(
        `/v1/social/whatsapp/conversations/${conversationId}/ai/sentiment`,
      ),
    ),
};

function toBackendScheduleType(type: WhatsAppScheduleRequest['type']): string {
  if (type === 'BIRTHDAY_CAMPAIGN') return 'BIRTHDAY';
  if (type === 'ANNIVERSARY_CAMPAIGN') return 'ANNIVERSARY';
  if (type === 'REMINDER_CAMPAIGN') return 'REMINDER';
  return type;
}

function fromBackendScheduleType(type: string): WhatsAppSchedule['type'] {
  if (type === 'BIRTHDAY') return 'BIRTHDAY_CAMPAIGN';
  if (type === 'ANNIVERSARY') return 'ANNIVERSARY_CAMPAIGN';
  if (type === 'REMINDER') return 'REMINDER_CAMPAIGN';
  return type as WhatsAppSchedule['type'];
}

function toBackendScheduleRequest(body: WhatsAppScheduleRequest) {
  return {
    name: body.name,
    scheduleType: toBackendScheduleType(body.type),
    socialAccountId: body.socialAccountId,
    templateVersionId: body.templateVersionId,
    audienceType: 'GROUP',
    groupId: body.groupId,
    contactIds: [],
    variables: body.variableMapping ?? {},
    timezone: body.timezone,
    localTime: body.localTime,
    startDate: body.startDate,
    endDate: body.endDate,
    rrule: body.rrule,
    triggerField: body.triggerField,
    dstPolicy: body.dstPolicy,
    misfirePolicy: body.misfirePolicy,
    maxOccurrences: 1000,
  };
}

function fromBackendSchedule(value: BackendWhatsAppSchedule): WhatsAppSchedule {
  return {
    id: value.id,
    name: value.name,
    type: fromBackendScheduleType(value.scheduleType),
    state: value.scheduleState,
    socialAccountId: value.socialAccountId,
    templateVersionId: value.templateVersionId,
    groupId: value.groupId,
    rrule: value.rrule,
    timezone: value.timezone,
    localTime: value.localTime,
    startDate: value.startDate,
    endDate: value.endDate,
    triggerField: value.triggerField,
    variableMapping: value.variables ?? {},
    dstPolicy: value.dstPolicy,
    misfirePolicy: value.misfirePolicy,
    nextRunAt: asUtc(value.nextRunAt),
    lastRunAt: asUtc(value.lastRunAt),
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
  };
}

function fromBackendOccurrence(value: BackendScheduleOccurrence): WhatsAppScheduleOccurrence {
  const utc = asUtc(value.utc) ?? value.utc;
  return {
    localDateTime: utc,
    utcDateTime: utc,
    offset: value.offset,
    adjusted: value.dstAdjusted,
    note: value.resolution,
  };
}

function fromBackendHistory(
  value: BackendScheduleHistory,
  scheduleId = 0,
): WhatsAppScheduleExecution {
  return {
    id: value.id,
    scheduleId,
    plannedAt: asUtc(value.plannedAt) ?? value.plannedAt,
    startedAt: asUtc(value.actualAt),
    state: value.state,
    recipientCount: value.recipientCount,
    sendJobId: value.sendJobId,
    errorMessage: value.errorMessage,
  };
}

function fromSpringPage<T, R>(page: SpringPage<T>, map: (value: T) => R): PageResponse<R> {
  return {
    content: page.content.map(map),
    page: page.number,
    size: page.size,
    totalElements: page.totalElements,
    totalPages: page.totalPages,
    first: page.first,
    last: page.last,
  };
}

function asUtc(value?: string | null): string | undefined {
  if (!value) return undefined;
  return /(?:Z|[+-]\d{2}:\d{2})$/.test(value) ? value : `${value}Z`;
}
